import { pathForPreviewSlug } from "@/lib/brand-site-preview-nav";

/** 组树用的页面字段；不依赖 html。 */
export type PageTreeItem = {
  id: number;
  slug: string;
  title: string;
  is_home: boolean;
};

export type PageTreeNode = {
  key: string;
  name: string;
  title: string;
  /** 有页面则可点；虚拟目录为 null */
  slug: string | null;
  path: string;
  kind?: string;
  isHome: boolean;
  pageId: number | null;
  children: PageTreeNode[];
};

export type PageTreeCollection = {
  key: string;
  nav_label: string;
};

function normalizeSlug(slug: string): string {
  return slug.trim().replace(/^\/+|\/+$/g, "");
}

function isHomePage(page: PageTreeItem, homeSlug: string): boolean {
  return page.is_home || normalizeSlug(page.slug) === normalizeSlug(homeSlug);
}

function compareNodes(a: PageTreeNode, b: PageTreeNode): number {
  if (a.isHome && !b.isHome) return -1;
  if (!a.isHome && b.isHome) return 1;
  return a.title.localeCompare(b.title, "zh");
}

function sortTree(node: PageTreeNode): void {
  node.children.sort(compareNodes);
  for (const child of node.children) sortTree(child);
}

function ensureChild(parent: PageTreeNode, segment: string): PageTreeNode {
  const existing = parent.children.find((child) => child.name === segment && !child.isHome);
  if (existing) return existing;
  const path = parent.path === "/" ? `/${segment}` : `${parent.path}/${segment}`;
  const child: PageTreeNode = {
    key: `dir:${path}`,
    name: segment,
    title: segment,
    slug: null,
    path,
    isHome: false,
    pageId: null,
    children: [],
  };
  parent.children.push(child);
  return child;
}

function findNodeBySlug(node: PageTreeNode, slug: string): PageTreeNode | undefined {
  if (node.slug === slug) return node;
  for (const child of node.children) {
    const hit = findNodeBySlug(child, slug);
    if (hit) return hit;
  }
  return undefined;
}

function isPartialSlug(slug: string): boolean {
  return normalizeSlug(slug).startsWith("_");
}

/**
 * 按 slug 路径组树。首页始终挂在 `/` 下第一项；
 * `about` + `about/team` 会嵌套；只有 `about/team` 时 `about` 为虚拟目录。
 * `_` 前缀页为部件，不进树；栏目列表为 `/blog*`，单篇模板为 `/blog/:slug`。
 */
export function buildPageTree(
  pages: PageTreeItem[],
  homeSlug: string,
  collections?: PageTreeCollection[],
): PageTreeNode {
  const visiblePages = pages.filter((page) => !isPartialSlug(page.slug));
  const root: PageTreeNode = {
    key: "dir:/",
    name: "/",
    title: "/",
    slug: homeSlug,
    path: "/",
    isHome: true,
    pageId: null,
    children: [],
  };

  for (const page of visiblePages) {
    const slug = normalizeSlug(page.slug);
    const title = page.title.trim() || slug || "未命名";

    if (isHomePage(page, homeSlug)) {
      root.children.push({
        key: `page:${page.id}`,
        name: title,
        title,
        slug: page.slug,
        path: "/",
        isHome: true,
        pageId: page.id,
        children: [],
      });
      continue;
    }

    const segments = slug.split("/").filter(Boolean);
    if (segments.length === 0) {
      root.children.push({
        key: `page:${page.id}`,
        name: title,
        title,
        slug: page.slug,
        path: pathForPreviewSlug(page.slug, homeSlug),
        isHome: false,
        pageId: page.id,
        children: [],
      });
      continue;
    }

    let cursor = root;
    for (let i = 0; i < segments.length; i += 1) {
      const segment = segments[i]!;
      cursor = ensureChild(cursor, segment);
      if (i === segments.length - 1) {
        cursor.key = `page:${page.id}`;
        cursor.slug = page.slug;
        cursor.title = title;
        cursor.path = pathForPreviewSlug(page.slug, homeSlug);
        cursor.pageId = page.id;
      }
    }
  }

  for (const collection of collections ?? []) {
    const listPath = `/${collection.key}*`;
    const itemTemplateSlug = `${collection.key}/:slug`;
    const itemTemplatePath = `/${itemTemplateSlug}`;
    const existing = findNodeBySlug(root, collection.key);
    if (existing) {
      existing.kind = "collection_list";
      existing.path = listPath;
      existing.title = collection.nav_label;
      existing.name = collection.nav_label;
      existing.slug = collection.key;
      existing.key = `collection:${collection.key}`;
    } else {
      root.children.push({
        key: `collection:${collection.key}`,
        name: collection.nav_label,
        title: collection.nav_label,
        slug: collection.key,
        path: listPath,
        kind: "collection_list",
        isHome: false,
        pageId: null,
        children: [],
      });
    }

    // 单篇模板记号（版式树，非真文）：与列表节点并列，便于地址栏预览皮
    const itemTemplateTitle = `${collection.nav_label} · 单篇`;
    const alreadyHasTemplate = root.children.some(
      (child) => child.kind === "collection_item" && child.slug === itemTemplateSlug,
    );
    if (!alreadyHasTemplate) {
      root.children.push({
        key: `collection-item:${collection.key}`,
        name: itemTemplateTitle,
        title: itemTemplateTitle,
        slug: itemTemplateSlug,
        path: itemTemplatePath,
        kind: "collection_item",
        isHome: false,
        pageId: null,
        children: [],
      });
    }
  }

  sortTree(root);
  return root;
}

function nodeMatches(node: PageTreeNode, query: string): boolean {
  return (
    node.title.toLowerCase().includes(query) ||
    node.name.toLowerCase().includes(query) ||
    node.path.toLowerCase().includes(query) ||
    (node.slug?.toLowerCase().includes(query) ?? false)
  );
}

function filterNode(node: PageTreeNode, query: string): PageTreeNode | null {
  if (nodeMatches(node, query)) return node;
  const children = node.children
    .map((child) => filterNode(child, query))
    .filter((child): child is PageTreeNode => child != null);
  if (children.length === 0) return null;
  return { ...node, children };
}

/**
 * 地址栏即筛选：空白或仍是当前路径时不过滤（弹出层打开时应看到整棵树）。
 * 用户改了地址栏内容后，用草稿去匹配标题 / 路径 / slug。
 */
export function addressBarTreeQuery(urlDraft: string, currentPath: string): string {
  const draft = urlDraft.trim();
  if (!draft || draft === currentPath) return "";
  return draft;
}

/** 实时过滤：匹配标题 / 路径 / slug；命中节点保留整棵子树，祖先一并留下。 */
export function filterPageTree(root: PageTreeNode, rawQuery: string): PageTreeNode {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return root;
  const children = root.children
    .map((child) => filterNode(child, query))
    .filter((child): child is PageTreeNode => child != null);
  return { ...root, children };
}
